import { useState } from 'react'
import {
  Settings,
  AlertCircle,
  CheckCircle,
  GitBranch,
  ChevronDown,
  Building,
  Lock,
  ClipboardList,
  Rocket,
  Target,
  RotateCcw,
  MapPin,
  Sparkles,
  Lightbulb,
  RefreshCw,
  FileText,
  Users,
  Briefcase,
  BarChart3,
  Palette,
  ArrowRight
} from 'lucide-react'
import AnchorLink from './AnchorLink'
import talentStatusesConfig from '../../../../shared/talentStatuses.json'

const SubSectionHeading = ({ children, id }) => (
  <div className="mb-6" id={id}>
    <div className="group flex items-center gap-2">
      <h3 className="wiki-heading text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
        {children}
      </h3>
      {id && <AnchorLink id={id} />}
    </div>
    <div className="mt-2 h-px bg-light-border-primary dark:bg-dark-border-primary" />
  </div>
)

const NewcomerCallout = ({ children }) => (
  <div className="my-6 pl-4 border-l-2 border-primary-500/40 bg-primary-50/50 dark:bg-primary-900/10 rounded-r-lg py-3 pr-4">
    {children}
  </div>
)

const Step = ({ number, icon: Icon, title, children }) => (
  <div className="relative pl-14 py-2">
    <span
      aria-hidden="true"
      className="wiki-mono absolute left-0 top-0 text-6xl font-bold leading-none select-none text-light-border-primary dark:text-dark-bg-tertiary"
    >
      {number}
    </span>
    <div className="absolute left-10 top-2 bottom-2 w-0.5 bg-primary-500 rounded-full" />
    <div className="pt-1">
      <h5 className="wiki-heading text-base font-semibold text-light-text-primary dark:text-dark-text-primary mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-primary-500 flex-shrink-0" />}
        {title}
      </h5>
      {children}
    </div>
  </div>
)

const InfoBox = ({ children }) => (
  <div className="mt-3 rounded-lg p-3 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary">
    {children}
  </div>
)

/**
 * Списки статусов берутся напрямую из shared/talentStatuses.json — того же файла,
 * по которому работает бэкенд. Правка в файле меняет и поведение системы, и эту
 * страницу: дублировать значения в вики нельзя, они разъедутся.
 */
const STATUS_GROUP_META = {
  INACTIVE_STATUSES: {
    title: 'Inactive statuses',
    desc: 'A talent in any of these is marked inactive: published posts are unpublished and the linked account is switched off.',
    accent: 'text-red-600 dark:text-red-400',
  },
  ACTIVE_STATUSES: {
    title: 'Active statuses',
    desc: 'Feeds the "Active Employees" section in the sidebar — people actually working or available.',
    accent: 'text-green-600 dark:text-green-400',
  },
  PROJECT_STATUSES: {
    title: 'Project statuses',
    desc: 'Feeds the "Project Employees" section in the sidebar.',
    accent: 'text-blue-600 dark:text-blue-400',
  },
  STATUSES_FOR_SITE: {
    title: 'Statuses shown on the site',
    desc: 'Only talents in these statuses reach the public catalog.',
    accent: 'text-purple-600 dark:text-purple-400',
  },
  TALENT_TYPES_FOR_SITE: {
    title: 'Talent types shown on the site',
    desc: 'Which talent types are exposed publicly.',
    accent: 'text-purple-600 dark:text-purple-400',
  },
  ONBOARDING_SUCCESS_STATUSES: {
    title: 'Onboarding — success',
    desc: 'Counted as a successful outcome in the onboarding funnel.',
    accent: 'text-green-600 dark:text-green-400',
  },
  ONBOARDING_FAILURE_STATUSES: {
    title: 'Onboarding — failure',
    desc: 'Counted as a failed outcome in the onboarding funnel.',
    accent: 'text-amber-600 dark:text-amber-400',
  },
}

const humanizeGroupKey = (key) =>
  key.toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

const StatusGroupCard = ({ groupKey, values }) => {
  const meta = STATUS_GROUP_META[groupKey]

  return (
    <div className="rounded-xl border border-light-border-primary dark:border-dark-border-primary p-4">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <span className={`wiki-heading text-sm font-semibold ${meta?.accent || 'text-light-text-primary dark:text-dark-text-primary'}`}>
          {meta?.title || humanizeGroupKey(groupKey)}
        </span>
        <span className="wiki-mono text-[10px] text-light-text-tertiary dark:text-dark-text-tertiary shrink-0">
          {values.length}
        </span>
      </div>

      <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mb-3">
        {meta?.desc || 'Defined in the shared configuration file.'}
      </p>

      <div className="flex flex-wrap gap-1.5">
        {values.map((value) => (
          <span
            key={value}
            className="wiki-mono text-[11px] px-2 py-0.5 rounded-md bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary"
          >
            {value}
          </span>
        ))}
      </div>

      <p className="wiki-mono text-[10px] text-light-text-tertiary dark:text-dark-text-tertiary mt-3">
        {groupKey}
      </p>
    </div>
  )
}

const MockupFrame = ({ label, children }) => (
  <div className="mt-4 rounded-xl border border-light-border-primary dark:border-dark-border-primary overflow-hidden">
    <div className="px-3 py-2 border-b border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
      <span className="wiki-mono text-[11px] text-light-text-tertiary dark:text-dark-text-tertiary">
        {label}
      </span>
    </div>
    <div className="p-4">
      {children}
    </div>
  </div>
)

const StatusSystemSection = () => {
  const [expandedBlocks, setExpandedBlocks] = useState({})

  const toggleBlock = (blockKey) => {
    setExpandedBlocks(prev => ({
      ...prev,
      [blockKey]: !prev[blockKey]
    }))
  }

  const faqItems = [
    {
      key: 'q1',
      question: "I don't see any statuses in the list. What should I do?",
      answer: (
        <ul className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-1">
          <li>• Check that statuses in <strong>Libraries Service</strong> have "talent-service" assigned</li>
          <li>• Wait for automatic synchronization (15 minutes) or ask admin to force sync</li>
          <li>• Verify that your account has access to Libraries Service</li>
          <li>• Check server logs for synchronization errors</li>
        </ul>
      )
    },
    {
      key: 'q2',
      question: 'Can I create a new status directly in Talent Service?',
      answer: (
        <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
          <p><strong className="text-light-text-primary dark:text-dark-text-primary">No.</strong> All statuses must be created in <strong>Libraries Service</strong> first. This ensures consistency across all company systems.</p>
          <p>In Talent Service, you can only configure <strong>talent types</strong>, <strong>descriptions</strong>, and <strong>transitions</strong> for existing statuses.</p>
        </div>
      )
    },
    {
      key: 'q3',
      question: "Why can't I change the status name or color?",
      answer: (
        <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
          <p>Status names and colors are managed centrally in <strong>Libraries Service</strong> to maintain consistency across all systems.</p>
          <p>If you need to change the name or color, contact your administrator or go to Libraries Service directly.</p>
        </div>
      )
    },
    {
      key: 'q4',
      question: "I assigned a talent type but it's not showing in Candidates/Employees sections. Why?",
      answer: (
        <ul className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-1">
          <li>• Make sure you saved the changes after assigning the talent type</li>
          <li>• Check if the status is marked as <strong>Active</strong></li>
          <li>• Verify that the talent type matches the section you're looking at</li>
          <li>• Refresh the page to ensure latest data is loaded</li>
        </ul>
      )
    },
    {
      key: 'q5',
      question: "What happens if I delete a status that's already assigned to talents?",
      answer: (
        <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
          <p><strong className="text-light-text-primary dark:text-dark-text-primary">Be careful!</strong> The system will prevent you from deleting a status that's currently assigned to any talents.</p>
          <p>You must first reassign all talents to different statuses before you can delete it. This prevents data integrity issues.</p>
        </div>
      )
    },
    {
      key: 'q6',
      question: 'How do transitions work? Do I need to set them up for every status?',
      answer: (
        <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
          <p>Transitions define which status changes are allowed. For example, "New Candidate" can transition to "Interview" but not directly to "Hired".</p>
          <ul className="space-y-1 mt-1">
            <li>• Click the <strong>Transitions button</strong> (arrow icon) on any status card</li>
            <li>• Select which statuses can follow the current one</li>
            <li>• Optionally require a reason for certain transitions</li>
            <li>• This creates a workflow that prevents invalid status changes</li>
          </ul>
        </div>
      )
    },
    {
      key: 'q7',
      question: 'Can one status be used for multiple talent types?',
      answer: (
        <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
          <p><strong className="text-light-text-primary dark:text-dark-text-primary">No.</strong> Each status can only be assigned to one talent type at a time (Candidate, Employee, or Presale).</p>
          <p>If you need similar statuses for different talent types, create separate statuses in Libraries Service (e.g., "Candidate - Active" and "Employee - Active").</p>
        </div>
      )
    },
    {
      key: 'q8',
      question: 'What does "Public Visible" mean?',
      answer: (
        <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
          <p>The "Public Visible" setting determines whether this status is visible in public-facing interfaces (like job application portals).</p>
          <ul className="space-y-1 mt-1">
            <li>• <strong>Public (👁️):</strong> Status can be seen by candidates/external users</li>
            <li>• <strong>Hidden (👁️‍🗨️):</strong> Status is only visible to internal users</li>
          </ul>
          <p>Example: "Rejected" might be hidden, while "Interview Scheduled" could be public.</p>
        </div>
      )
    }
  ]

  const proTips = [
    { icon: Lightbulb, title: 'Use descriptive names', body: 'Make status names clear and self-explanatory so anyone on the team understands what they mean' },
    { icon: RefreshCw, title: 'Plan your workflow', body: 'Before setting up transitions, map out your hiring/employee workflow on paper' },
    { icon: BarChart3, title: 'Use display order', body: 'Set display order to control how statuses appear in dropdowns and lists' },
    { icon: Palette, title: 'Color coding', body: 'Use colors consistently: green for positive, red for negative, yellow for pending states' },
  ]

  const quickSummary = [
    { trigger: 'Empty status list?', action: 'Go to Libraries Service and assign "talent-service" to statuses' },
    { trigger: 'Statuses appeared?', action: 'Assign a status type (Candidate, Employee, Presale, etc.) via the Edit button' },
    { trigger: 'Want to change statuses?', action: 'Configure transitions by clicking the Transitions button' },
    { trigger: 'Need help?', action: 'Contact your administrator for access to Libraries Service' },
  ]

  const whatNext = [
    {
      icon: ClipboardList,
      title: 'Explore other Wiki sections',
      items: ['System architecture', 'Authentication and security', 'Working with REST API']
    },
    {
      icon: Rocket,
      title: 'Start working',
      items: ['Go to the Candidates section', 'Check available statuses', 'Start working with talents']
    }
  ]

  return (
    <div className="space-y-10">

      {/* What is it */}
      <div>
        <SubSectionHeading id="statuses-intro">What is it?</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">
          The status system defines the stages of working with talents in your company. Each candidate, employee or presale goes through certain stages:
          from first contact to hiring or completion of cooperation.
        </p>
      </div>

      {/* Quick Start Guide */}
      <div>
        <SubSectionHeading id="statuses-quickstart">Quick Start Guide: Setting Up Your First Statuses</SubSectionHeading>

        <NewcomerCallout>
          <p className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary mb-1">
            New to the system? Start here.
          </p>
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            If you just logged into the project and see an empty status list, don't worry! Follow these simple steps to get started.
          </p>
        </NewcomerCallout>

        <div className="space-y-8 mt-6">
          {/* Step 1 */}
          <Step number="1" icon={Building} title="Go to Libraries Service">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              Navigate to the company's{' '}
              <strong>
                <a href="https://libs.anyemp.com/" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-600 underline underline-offset-2">
                  Libraries microservice
                </a>
              </strong>{' '}
              (central status repository).
            </p>
            <InfoBox>
              <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Where to find it:
              </p>
              <ul className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-1 ml-4">
                <li>• Open the <strong>Statuses</strong> tab in Libraries Service</li>
                <li>• Look for the list of all company statuses</li>
              </ul>
            </InfoBox>
          </Step>

          {/* Step 2 */}
          <Step number="2" icon={Settings} title='Assign "talent-service" to Statuses'>
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              For each status you want to use, assign the{' '}
              <code className="px-1.5 py-0.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded text-xs wiki-mono border border-light-border-primary dark:border-dark-border-primary">
                talent-service
              </code>{' '}
              designation.
            </p>
            <InfoBox>
              <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Why is this needed?
              </p>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                This tells the system which statuses should be synchronized and displayed in the Talent Service interface.
              </p>
            </InfoBox>
            <InfoBox>
              <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1 flex items-center gap-2">
                <Lock className="w-4 h-4" /> No access to the Statuses table?
              </p>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Contact your system administrator to get the necessary permissions or ask them to configure the statuses for you.
              </p>
            </InfoBox>
          </Step>

          {/* Step 3 */}
          <Step number="3" icon={RotateCcw} title="Return to Talent Service and Set the Status Type">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              After synchronization (within 15 minutes or force sync), return to the <strong>Statuses</strong> page and assign a type to each status — this determines which section of the system uses it.
            </p>

            <InfoBox>
              <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2 flex items-center gap-2">
                <Target className="w-4 h-4" /> Available Status Types:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { icon: Users,     label: 'Candidate',       color: 'text-blue-500' },
                  { icon: Briefcase, label: 'Employee',         color: 'text-green-500' },
                  { icon: Target,    label: 'Presale',          color: 'text-purple-500' },
                  { icon: FileText,  label: 'Job Application',  color: 'text-orange-500' },
                  { icon: Palette,   label: 'Talent Template',  color: 'text-pink-500' },
                  { icon: Rocket,    label: 'Talent Post',      color: 'text-indigo-500' },
                ].map(({ icon: Icon, label, color }) => (
                  <div key={label} className="flex items-center gap-1.5 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                    <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${color}`} />
                    {label}
                  </div>
                ))}
              </div>
            </InfoBox>

            <MockupFrame label="Status card example">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" style={{ backgroundColor: '#3B82F6' }} />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">New Candidate</h3>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors duration-150 relative" title="Edit (description and settings only)">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                    </button>
                    <button className="p-2 text-gray-500 dark:text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors duration-150" title="Transitions">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                    </button>
                    <button className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors duration-150" title="Delete">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200">
                      <span className="w-2 h-2 rounded-full bg-current mr-2" />
                      Type: Candidate
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">Initial status for new candidates entering the hiring process</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span className="text-green-600 font-medium">Active</span>
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      <span className="text-green-600">Public</span>
                    </div>
                    <span>Order: 1</span>
                  </div>
                  <div>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-1.5" />
                      Library ID: LS-2024-001
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-2 items-start">
                <Lightbulb className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                  You can only edit the <strong>description</strong> and <strong>status type</strong> of a status. The name and color come from Libraries Service.
                </p>
              </div>
            </MockupFrame>
          </Step>

          {/* Step 4 */}
          <Step number="4" icon={GitBranch} title="Configure Status Transitions">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              To change a talent's status, you need to set up allowed transitions between statuses.
            </p>

            <InfoBox>
              <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2 flex items-center gap-2">
                <RefreshCw className="w-4 h-4" /> How to manage transitions:
              </p>
              <ul className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-1 ml-4">
                <li>• Click the <strong>Transitions button</strong> (arrow icon) on any status card</li>
                <li>• Define which statuses can transition to each other</li>
                <li>• Set transition requirements (e.g., reason required, approval needed)</li>
              </ul>
            </InfoBox>

            <MockupFrame label="Transitions example">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" style={{ backgroundColor: '#8B5CF6' }} />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Interview</h3>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors duration-150" title="Edit">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                    </button>
                    <button className="p-2 text-gray-500 dark:text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors duration-150 relative" title="Transitions">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full animate-pulse" />
                    </button>
                    <button className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors duration-150" title="Delete">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200">
                    <span className="w-2 h-2 rounded-full bg-current mr-2" />
                    Type: Candidate
                  </span>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">Candidate is currently in the interview process</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-green-600 font-medium">Active</span>
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      <span className="text-green-600">Public</span>
                    </div>
                    <span className="text-gray-500 dark:text-gray-400">Order: 2</span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-2 items-start">
                <Lightbulb className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary space-y-0.5">
                  <p className="font-medium text-light-text-primary dark:text-dark-text-primary">What you can configure:</p>
                  <ul className="space-y-0.5">
                    <li>• Which statuses can follow this one</li>
                    <li>• Whether a reason is required for transition</li>
                    <li>• Additional validation rules</li>
                  </ul>
                </div>
              </div>
            </MockupFrame>

            <div className="mt-4">
              <p className="wiki-mono text-[11px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary mb-2">
                Example transition flow:
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {['New', 'Interview', 'Offer', 'Hired'].map((label, i, arr) => (
                  <span key={label} className="flex items-center gap-2">
                    <span className="wiki-mono text-xs px-2.5 py-1 rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border-primary dark:border-dark-border-primary text-light-text-primary dark:text-dark-text-primary">
                      {label}
                    </span>
                    {i < arr.length - 1 && (
                      <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-xs">→</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </Step>

          {/* Step 5 */}
          <Step number="5" icon={CheckCircle} title="Start Using the System!">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              Once statuses are configured, you can start managing talents.
            </p>
            <InfoBox>
              <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2 flex items-center gap-2">
                <Rocket className="w-4 h-4" /> Next steps:
              </p>
              <ul className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-1 ml-4">
                <li>• Navigate to Candidates, Employees, or Presales sections</li>
                <li>• Add new talents and assign them statuses</li>
                <li>• Change talent statuses using the configured transitions</li>
                <li>• Monitor your talent pipeline</li>
              </ul>
            </InfoBox>
          </Step>
        </div>

        {/* Quick Summary */}
        <div className="mt-8">
          <p className="wiki-mono text-[11px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary mb-3 flex items-center gap-2">
            <ClipboardList className="w-3.5 h-3.5" /> Quick Summary
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {quickSummary.map(tip => (
              <div key={tip.trigger} className="p-3 rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
                <p className="wiki-mono text-xs text-primary-500 font-medium mb-1">{tip.trigger}</p>
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{tip.action}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status lists from the shared config */}
      <div>
        <SubSectionHeading id="statuses-lists">Status lists</SubSectionHeading>

        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-2">
          Several parts of the system do not care about a status by name, but about the group it
          belongs to — whether it counts as inactive, whether it reaches the public site, whether it
          closes the onboarding funnel. Those groups live in one shared configuration file.
        </p>

        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-5">
          The lists below are read straight from that file, the same one the backend uses. Edit it and
          both the behaviour and this page change together — nothing is duplicated here by hand.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(talentStatusesConfig).map(([groupKey, values]) => (
            <StatusGroupCard
              key={groupKey}
              groupKey={groupKey}
              values={Array.isArray(values) ? values : []}
            />
          ))}
        </div>

        <InfoBox>
          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
            Names must match the status exactly as it is spelled in the statuses dictionary — the match
            is by name, not by ID. A status that is not in any list is simply treated as an ordinary
            active one.
          </p>
        </InfoBox>
      </div>

      {/* FAQ */}
      <div>
        <SubSectionHeading id="statuses-faq">Common Questions & Troubleshooting</SubSectionHeading>

        <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
          {faqItems.map(({ key, question, answer }) => (
            <div key={key} id={key} className="scroll-mt-6 group">
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button
                    onClick={() => toggleBlock(key)}
                    className="text-sm font-medium text-left text-light-text-primary dark:text-dark-text-primary hover:text-primary-500 transition-colors"
                  >
                    {question}
                  </button>
                  <AnchorLink id={key} />
                </div>
                <button
                  onClick={() => toggleBlock(key)}
                  className="flex-shrink-0 ml-4"
                >
                  <ChevronDown
                    className={[
                      'w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary transition-transform duration-200',
                      expandedBlocks[key] ? 'rotate-180' : ''
                    ].join(' ')}
                  />
                </button>
              </div>
              {expandedBlocks[key] && (
                <div className="pb-4 pr-6">
                  {answer}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Pro Tips */}
        <div className="mt-8">
          <p className="wiki-mono text-[11px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary mb-3 flex items-center gap-2">
            <Rocket className="w-3.5 h-3.5" /> Pro Tips
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {proTips.map(tip => (
              <div key={tip.title} className="flex gap-3 p-4 rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
                <div className="w-8 h-8 rounded-md bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
                  <tip.icon className="w-4 h-4 text-primary-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary mb-0.5">{tip.title}</p>
                  <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">{tip.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What's next */}
      <div>
        <SubSectionHeading id="statuses-next">What you can do next</SubSectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {whatNext.map(item => (
            <div key={item.title} className="group p-4 rounded-xl border border-light-border-primary dark:border-dark-border-primary hover:border-primary-300 dark:hover:border-primary-700 transition-colors bg-light-bg-primary dark:bg-dark-bg-primary">
              <div className="flex items-center gap-2 mb-2">
                <item.icon className="w-4 h-4 text-primary-500 flex-shrink-0" />
                <h5 className="wiki-heading text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">
                  {item.title}
                </h5>
                <ArrowRight className="w-3.5 h-3.5 ml-auto text-light-text-tertiary dark:text-dark-text-tertiary group-hover:text-primary-500 group-hover:translate-x-0.5 transition-all" />
              </div>
              <ul className="space-y-1">
                {item.items.map(i => (
                  <li key={i} className="text-xs text-light-text-secondary dark:text-dark-text-secondary">• {i}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

    </div>
  )
}

export default StatusSystemSection
